/* eslint react-hooks/exhaustive-deps: "warn" */
import { useEffect, useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './TransferPricingClassification.module.scss';

import { type CodeRow, type DecisionRow } from './TransferPricingClassification.types';

type Translate = (key: string) => string;

const CODE_ROWS: CodeRow[] = [
  { jurisdiction: 'Germany', flag: '🇩🇪', system: 'WZ', code: '46.73' },
  { jurisdiction: 'Italy', flag: '🇮🇹', system: 'ATECO 2025', code: '46.73.0' },
  { jurisdiction: 'France', flag: '🇫🇷', system: 'NAF', code: '46.73A' },
  { jurisdiction: 'Poland', flag: '🇵🇱', system: 'PKD', code: '46.73.Z' },
  { jurisdiction: 'Netherlands', flag: '🇳🇱', system: 'SBI', code: '4673' },
  { jurisdiction: 'United Kingdom', flag: '🇬🇧', system: 'SIC 2007', code: '46730' },
];

const getDecisions = (t: Translate): DecisionRow[] => [
  {
    action: t('For one national tax authority, search on the code its own registry filed.'),
    systems: 'WZ · ATECO · NAF · PKD · SBI · SIC',
    rationale: t(
      'The examiner works in their own system. They expect the code their registry holds, at the vintage it held it.',
    ),
  },
  {
    action: t('For several countries at once, search on NACE Rev. 2.1 or on our own class.'),
    rationale: t(
      'Both are harmonised to four digits, and our own class carries beyond the EU. They are the only layers that mean the same thing in every country, because national detail below four digits does not travel.',
    ),
  },
  {
    action: t('For a functional comparability test, search on the activity tags and the described business.'),
    rationale: t(
      'A code records how a company registered itself, often decades ago. Tags record what it says it does now, and we hold the LinkedIn industry alongside them as a cross-check.',
    ),
  },
];

export const TransferPricingClassification = () => {
  const { t } = useInt();
  const decisions = getDecisions(t);
  const headRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const revealables = [headRef.current, cardRef.current, ...rowRefs.current].filter((el): el is HTMLDivElement =>
      Boolean(el),
    );

    if (!revealables.length) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.revealed);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 },
    );

    revealables.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <Section background="#F6F7F9" className={styles.section}>
      <div className={styles.head} ref={headRef}>
        <TitleBlock
          align="left"
          className={styles.titleBlock}
          title={t('The same activity is coded six different ways.')}
          description={t(
            'Every registry uses its own national system. Screen a cross-border population on any single one of them and five of these companies never enter it.',
          )}
        />
      </div>

      <div className={styles.registry} ref={cardRef}>
        <div className={styles.registryHead}>
          <span className={styles.registryTitle}>{t('Wholesale of building materials')}</span>
          <span className={styles.registrySubtitle}>{t('one activity, filed under six national systems')}</span>
        </div>

        <div className={styles.table}>
          <div className={styles.tableHead}>
            <span>{t('Jurisdiction')}</span>
            <span>{t('National system')}</span>
            <span>{t('Code as filed')}</span>
          </div>

          {CODE_ROWS.map((row) => (
            <div key={row.jurisdiction} className={styles.tableRow}>
              <span className={styles.jurisdiction}>
                <span aria-hidden="true">{row.flag}</span>
                {row.jurisdiction}
              </span>
              <span className={styles.system}>{row.system}</span>
              <span className={styles.code}>{row.code}</span>
            </div>
          ))}
        </div>

        <div className={styles.resolves}>
          <div className={styles.resolvesStat}>
            <b>6</b>
            <span className={styles.resolvesArrow}>→</span>
            <b>1</b>
            <span className={styles.resolvesLabel}>{t('codes resolved')}</span>
          </div>

          <div className={styles.resolvesRow}>
            <span className={styles.resolvesKey}>NACE Rev. 2.1</span>
            <span className={styles.resolvesValue}>46.73</span>
          </div>

          <div className={styles.resolvesRow}>
            <span className={styles.resolvesKey}>Global Database class</span>
            <span className={cn(styles.resolvesValue, styles.resolvesValueWide)}>
              Building materials &amp; supplies, wholesale
            </span>
          </div>
        </div>

        <p className={styles.registryFooter}>
          {t('Each code is held')} <b>{t('as the registry filed it')}</b>
          {t(
            ', at the vintage it filed it, next to the resolution. A screen can run on whichever layer the jurisdiction expects.',
          )}
        </p>
      </div>

      <div className={styles.pick}>
        <TitleBlock
          className={styles.pickTitleBlock}
          title={t('So which layer do you search on?')}
          description={t(
            'Every company we hold carries all three at once: the code its own registry filed, the harmonised equivalent, and what the business says it actually does. The one you search on depends on who has to accept the set.',
          )}
        />

        <div className={styles.decisions}>
          {decisions.map((decision, index) => (
            <div
              key={decision.action}
              className={styles.decision}
              style={{ transitionDelay: `${index * 90}ms` }}
              ref={(el) => {
                rowRefs.current[index] = el;
              }}
            >
              <span className={styles.decisionNum}>{String(index + 1).padStart(2, '0')}</span>

              <div>
                <div className={styles.decisionAction}>{decision.action}</div>

                {decision.systems && <div className={styles.decisionSystems}>{decision.systems}</div>}

                <p className={styles.decisionRationale}>{decision.rationale}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
};
